// src/api/reviewsApi.js

import { auth } from '../firebaseConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth token
const getAuthToken = async () => {
  try {
    const currentUser = auth.currentUser;
    if (currentUser) {
      return await currentUser.getIdToken();
    }
  } catch (error) {
    console.error('Error getting auth token:', error);
  }
  return null;
};

export const reviewsApi = {
  /**
   * Get reviews for equipment
   */
  getReviews: async (equipmentId, options = {}) => {
    try {
      const queryParams = new URLSearchParams(
        Object.fromEntries(
          Object.entries(options).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
        )
      ).toString();
      
      const url = `${API_URL}/api/equipment-reviews/equipment/${equipmentId}${queryParams ? `?${queryParams}` : ''}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching reviews:', error);
      throw error;
    }
  },
  
  /**
   * Get user's reviews
   */
  getUserReviews: async (userId, page = 1, limit = 10) => {
    try {
      const response = await fetch(
        `${API_URL}/api/equipment-reviews/user/${userId}?page=${page}&limit=${limit}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching user reviews:', error);
      throw error;
    }
  },
  
  /**
   * Create a new review
   */
  createReview: async (reviewData) => {
    try {
      const token = await getAuthToken();
      
      const response = await fetch(`${API_URL}/api/equipment-reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(reviewData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Failed to create review');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating review:', error);
      throw error;
    }
  },
  
  /**
   * Update an existing review
   */
  updateReview: async (reviewId, reviewData) => {
    try {
      const token = await getAuthToken();
      
      const response = await fetch(`${API_URL}/api/equipment-reviews/${reviewId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(reviewData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Failed to update review');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating review:', error);
      throw error;
    }
  },
  
  /**
   * Delete a review
   */
  deleteReview: async (reviewId) => {
    try {
      const token = await getAuthToken();
      
      const response = await fetch(`${API_URL}/api/equipment-reviews/${reviewId}`, {
        method: 'DELETE',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Failed to delete review');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error deleting review:', error);
      throw error;
    }
  },
  
  /**
   * Vote on a review (helpful or not helpful)
   */
  voteReview: async (reviewId, vote) => {
    try {
      const token = await getAuthToken();
      
      const response = await fetch(`${API_URL}/api/equipment-reviews/${reviewId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ vote })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Failed to vote on review');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error voting on review:', error);
      throw error;
    }
  },
  
  /**
   * Report a review
   */
  reportReview: async (reviewId, reason) => {
    try {
      const token = await getAuthToken();
      
      const response = await fetch(`${API_URL}/api/equipment-reviews/${reviewId}/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ reason })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Failed to report review');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error reporting review:', error);
      throw error;
    }
  },
  
  /**
   * Add sponsor response to a review
   */
  addSponsorResponse: async (reviewId, text) => {
    try {
      const token = await getAuthToken();
      
      const response = await fetch(`${API_URL}/api/equipment-reviews/${reviewId}/sponsor-response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ text })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Failed to add sponsor response');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error adding sponsor response:', error);
      throw error;
    }
  }
};

export default reviewsApi;

